import { createContext, useContext, useEffect, useState } from "react";

import { useIdleTimeout } from "../hooks/useIdleTimeout";

import { useApi } from "./ApiContext";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "http://localhost:8080/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const api = useApi();

  useEffect(() => {
    const validateToken = async () => {
      if (token) {
        try {
          // Token is passed via ApiContext automatically
          const response = await api.get(`/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error("Token validation failed:", error);
          localStorage.removeItem("token");
          setToken(null);
        }
      }
      setLoading(false);
    };

    validateToken();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await api.post(`/auth/login`, {
        email,
        password
      });
      const { access_token, user: userData } = response.data;
      localStorage.setItem("token", access_token);
      setToken(access_token);
      setUser(userData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Login failed"
      };
    }
  };

  const clearSession = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Backend logout failed:", error);
    } finally {
      clearSession();
    }
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await api.get(`/auth/me`);
        setUser(response.data);
        return response.data;
      } catch (error) {
        console.error("Failed to refresh user:", error);
      }
    }
  };

  // Handle unauthorized event globally
  useEffect(() => {
    const handleUnauthorized = () => {
      console.log("Unauthorized event received. Clearing session...");
      clearSession();
    };

    window.addEventListener("unauthorized", handleUnauthorized);
    return () => {
      window.removeEventListener("unauthorized", handleUnauthorized);
    };
  }, []);

  // Idle session detection and verification
  useIdleTimeout(token, user, api, clearSession);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        refreshUser,
        isAuthenticated: !!token && !!user,
        API // Exporting API URL for convenience
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
